import { FC, useCallback, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import {
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItems,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "~/application/components/DropdownMenu";
import { AGENCY_CUSTOMER_ID } from "~/application/constants/storageKeys";
import { Customer, UserContext } from "~/application/types";
import { authService, customerService } from "~/application/usecases";
import { asCNPJ } from "~/application/utils/mask-functions";
import { Box } from "~/components/Box";
import { CardSectionHeader } from "~/components/Card";
import { dialogService } from "~/components/DialogStack";
import { Flex } from "~/components/Flex";
import { Icon } from "~/components/Icon";
import { SvgChevronRight, SvgPerson } from "~/components/Icon/icons";
import { SearchBar } from "~/components/Input";
import { snackbarService } from "~/components/SnackbarStack";
import { Spinner } from "~/components/Spinner";
import { Text } from "~/components/Text";
import { useAuth } from "~/presentation/core/contexts/AuthContext";
import { useUser } from "~/presentation/core/contexts/UserContext";
import { ListItem } from "~/presentation/shared/components/ListItem";
import useMobile from "~/presentation/shared/hooks/useMobile";
import { useQueryCustomers } from "~/presentation/shared/hooks/useQueryCustomers";
import { RequestPasswordChangeDialog } from "~/presentation/shared/views/ChangePasswordDialog";
import { RequestAgencyPasswordChangeDialog } from "~/presentation/shared/components/RequestAgencyPasswordChangeDialog";
import { ProfileCard } from "./ProfileCard";
import { LoadingDialog } from "~/presentation/shared/views/LoadingDialog/LoadingDialog";
import { logError } from "~/presentation/shared/utils/errors";
import { CreditLimit } from "../CreditLimits/CreditLimit";
import { QueryKeys } from "~/constants";
import { useNavigate } from "react-router-dom";
import { sidebarService } from "~/services/SidebarService";
import { SidebarCustomerList } from "./ProfileCard/components/SidebarCustomerList";
import { FakeSearchToggle } from "./components/FakeSearchToggle";
import { getEnvMode } from "~/utils/env";

export const SNACKBAR_MESSAGES = {
  REQUEST_PASSWORD_CHANGE_SUCCESS_MESSAGE: "Solicitação de alteração de senha feita com sucesso",
  REQUEST_PASSWORD_CHANGE_ERROR_MESSAGE: "Falha ao solicitar alteração de senha",
  REQUEST_AGENCY_PASSWORD_CHANGE_SUCCESS_MESSAGE:
    "Solicitação de alteração de senha enviada com sucesso!",
  REQUEST_AGENCY_PASSWORD_CHANGE_ERROR_MESSAGE:
    "Erro ao solicitar alteração de senha. Tente novamente.",
  CUSTOMER_LOAD_ERROR_MESSAGE: " Falha ao carregar a empresa",
} as const;

const LOG_TAG = "AppContainer/ProfileMenu";

export const ProfileMenu: FC = () => {
  const navigate = useNavigate();
  const { user, contexts, setUserContext } = useUser();
  const { signOut } = useAuth();

  const customerId = localStorage.getItem(AGENCY_CUSTOMER_ID);
  const isMobile = useMobile();
  const isProduction = import.meta.env.VITE_ENV === "prod" || getEnvMode() === "prod";

  const { customers, searchText, setSearchText } = useQueryCustomers({
    options: { enabled: !!contexts.agency },
  });

  const { data: customer } = useQuery(
    [QueryKeys.CUSTOMER, user?.agency ?? contexts?.agency, customerId],
    () => customerService.findById(customerId!),
    {
      enabled: !!customerId,
      onError: (error) => {
        logError({
          defaultErrorMessage: SNACKBAR_MESSAGES.CUSTOMER_LOAD_ERROR_MESSAGE,
          error,
          logTag: LOG_TAG,
        });
      },
    },
  );

  const setCustomerContext = useCallback(
    (customer?: Customer, isEffect?: boolean) => {
      if (customerId && customerId === customer?.uuid && !isEffect) {
        setUserContext({ customer: undefined });
        localStorage.removeItem(AGENCY_CUSTOMER_ID);
        return;
      }
      setUserContext({
        customer: customer?.uuid !== contexts.customer?.uuid ? customer : undefined,
      });

      localStorage.setItem(AGENCY_CUSTOMER_ID, customer?.uuid ?? "");
    },
    [contexts, setUserContext, customerId],
  );

  useEffect(() => {
    if (contexts.agency && !contexts.customer && customer) {
      setCustomerContext(customer, true);
    } else if (!contexts.agency && !contexts.customer) {
      setCustomerContext(user.customer, true);
    }
  }, [contexts.agency, contexts.customer, customer, customerId, user, setCustomerContext]);

  const handleSignOut = useCallback(() => {
    signOut();
    setCustomerContext(undefined);
  }, [signOut]);

  const canChangePassword = user.context === UserContext.Customer;
  const canChangeAgencyPassword = user.context === UserContext.Agency;

  const { mutate: mutateRequestPasswordChange } = useMutation(
    async () => await authService.requestChangePassword(user.profiles.customer.uuid),
    {
      onMutate: () => {
        dialogService.popDialog();
        dialogService.showDialog(<LoadingDialog message="Solicitando alteração de senha" />);
      },
      onSuccess: () => {
        snackbarService.showSnackbar(
          SNACKBAR_MESSAGES.REQUEST_PASSWORD_CHANGE_SUCCESS_MESSAGE,
          "success",
        );
      },
      onError: (error) => {
        logError({
          error,
          logTag: LOG_TAG,
          defaultErrorMessage: SNACKBAR_MESSAGES.REQUEST_PASSWORD_CHANGE_ERROR_MESSAGE,
        });
      },
      onSettled: () => {
        dialogService.popDialog();
      },
    },
  );

  const { mutate: mutateRequestAgencyPasswordChange } = useMutation(
    async (secretCode: string) =>
      await authService.changeAgencyEmployeePassword({
        agencyEmployeeId: user.profiles.agency?.uuid || "",
        secret: secretCode,
      }),
    {
      onMutate: () => {
        dialogService.popDialog();
        dialogService.showDialog(<LoadingDialog message="Solicitando alteração de senha" />);
      },
      onSuccess: () => {
        snackbarService.showSnackbar(
          SNACKBAR_MESSAGES.REQUEST_AGENCY_PASSWORD_CHANGE_SUCCESS_MESSAGE,
          "success",
        );
      },
      onError: (error) => {
        logError({
          error,
          logTag: LOG_TAG,
          defaultErrorMessage: SNACKBAR_MESSAGES.REQUEST_AGENCY_PASSWORD_CHANGE_ERROR_MESSAGE,
        });
      },
      onSettled: () => {
        dialogService.popDialog();
      },
    },
  );

  const handleRequestPasswordChange = useCallback(() => {
    dialogService.showDialog(
      <RequestPasswordChangeDialog onSubmit={mutateRequestPasswordChange} />,
    );
  }, [mutateRequestPasswordChange]);

  const handleRequestAgencyPasswordChange = useCallback(() => {
    dialogService.showDialog(
      <RequestAgencyPasswordChangeDialog onSubmit={mutateRequestAgencyPasswordChange} />,
    );
  }, [mutateRequestAgencyPasswordChange]);

  const onSetCustomerContextClick = () => setCustomerContext(user.customer, true);

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Box>
            {isMobile ? (
              <SvgPerson />
            ) : (
              <ProfileCard onSetCustomerContextClick={onSetCustomerContextClick} />
            )}
          </Box>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" style={{ display: "block" }}>
          <DropdownMenuItems>
            {user.customer ? <CreditLimit /> : null}

            {!contexts.agency && (
              <DropdownMenuItem onClick={() => navigate("/perfil")}>
                <Text>Perfil</Text>
              </DropdownMenuItem>
            )}

            {canChangePassword && (
              <DropdownMenuItem onClick={handleRequestPasswordChange}>
                <Text>Alterar senha</Text>
              </DropdownMenuItem>
            )}

            {canChangeAgencyPassword && (
              <DropdownMenuItem onClick={handleRequestAgencyPasswordChange}>
                <Text>Alterar senha</Text>
              </DropdownMenuItem>
            )}

            {!!contexts.agency && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger asChild>
                  <DropdownMenuButton
                    onClick={
                      isMobile
                        ? () =>
                            sidebarService.showSidebar(
                              <SidebarCustomerList setCustomerContext={setCustomerContext} />,
                              {
                                width: isMobile ? "90vw" : "400px",
                                position: "right",
                              },
                            )
                        : undefined
                    }
                  >
                    <Text css={{ flexGrow: 1 }}>Trocar Empresa</Text>
                    <Icon as={SvgChevronRight} />
                  </DropdownMenuButton>
                </DropdownMenuSubTrigger>

                <DropdownMenuSubContent sideOffset={16} style={{ display: "block" }}>
                  <CardSectionHeader>
                    <Text>Empresas</Text>
                  </CardSectionHeader>

                  <Box
                    css={{
                      maxHeight: 350,
                      overflowY: "auto",
                    }}
                  >
                    <Box
                      css={{
                        p: "$4",
                        borderBottom: "1px solid $colors$neutrals-light",
                      }}
                    >
                      <SearchBar
                        search={searchText}
                        onSearchChange={setSearchText}
                        placeholder="Procurar empresa"
                        style={{ width: "100%" }}
                      />
                    </Box>
                    {!customers ? (
                      <Flex align="center" justify="center" css={{ p: "$4" }}>
                        <Spinner />
                      </Flex>
                    ) : customers.data.length === 0 ? (
                      <Box css={{ p: "$4" }}>
                        {searchText.length > 0 ? (
                          <Text>Nenhuma empresa corresponde aos dados informados</Text>
                        ) : (
                          <Text>Você não possui empresas cadastradas</Text>
                        )}
                      </Box>
                    ) : (
                      customers.data.map((item) => (
                        <ListItem
                          title={item.tradingName}
                          description={asCNPJ(item.cnpj)}
                          onClick={() => setCustomerContext(item)}
                          isSelected={contexts.customer?.uuid === item.uuid}
                          key={item.uuid}
                        />
                      ))
                    )}
                  </Box>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}

            {!isProduction && <FakeSearchToggle />}

            <DropdownMenuItem onClick={handleSignOut}>
              <Text data-cy="sign-out-button">Sair</Text>
            </DropdownMenuItem>
          </DropdownMenuItems>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
